import {
    Component,
    ChangeDetectionStrategy,
    signal,
    computed,
    inject,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Input,
    effect,
} from '@angular/core'
import { Router } from '@angular/router'
import { Subject, Subscription } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { IDeal, IFilterOption, IDealsPipelineApiItem } from 'src/app/core/models/dashboard.model'
import { allNationalStages } from 'src/app/core/constants/app.constants'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import { UserStore } from '@core/services/user/user.store'
import { SalesFunnelHelper } from 'src/app/core/helpers/sales-funnel.helper'
import {
    EStageId,
    EDealsPipelineStageRange,
    EDealsPipelineFilterType,
} from 'src/app/core/enum/sales-funnel.enum'
import { valueConstants } from 'src/app/core/constants/valueConstants'
import { AppConstants } from 'src/app/core/constants/app.constants'
import { ETableSortDirection } from '@app/core/enum/table.enum'
import { DatePipe } from '@angular/common'
import {
    HOME_DROPDOWN_SOURCE,
    HomeDashboardDropdownCoordinatorService,
} from '../../services/home-dashboard-dropdown.coordinator'
import { ZardButtonComponent } from '@/styles/components/button'

@Component({
    selector: 'app-deals-pipeline',
    templateUrl: './deals-pipeline.component.html',
    styleUrls: ['./deals-pipeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, ZardButtonComponent],
})
export class DealsPipelineComponent implements OnInit, OnDestroy, OnChanges {
    private readonly dashboardService = inject(IDashboardService)
    private readonly userStore = inject(UserStore)
    private readonly router = inject(Router)
    private readonly dropdownCoordinator = inject(HomeDashboardDropdownCoordinatorService)
    private readonly destroy$ = new Subject<void>()
    private dealsPipelineSubscription?: Subscription
    private lastAcademicYearStart: number | null = null

    @Input() isTeamView = false

    readonly valueConstants = valueConstants
    readonly EDealsPipelineFilterType = EDealsPipelineFilterType
    readonly ETableSortDirection = ETableSortDirection

    readonly dealSkeletonRows = [1, 2, 3, 4, 5]

    // Filter states
    stageFilter = signal<string>(valueConstants.ALL_STAGES)
    productFilter = signal<string>(valueConstants.ALL_PRODUCTS)
    cityFilter = signal<string>(valueConstants.ALL_CITIES)
    schoolNameSearch = signal<string>('')

    // Dropdown states
    isStageDropdownOpen = signal<boolean>(false)
    isProductDropdownOpen = signal<boolean>(false)
    isCityDropdownOpen = signal<boolean>(false)

    // Data states
    private readonly _deals = signal<IDeal[]>([])
    private readonly _isLoading = signal<boolean>(false)
    private readonly _error = signal<string | null>(null)

    readonly deals = this._deals.asReadonly()
    readonly isLoading = this._isLoading.asReadonly()
    readonly error = this._error.asReadonly()

    // Filter options
    readonly stageOptions = signal<IFilterOption[]>([
        { label: valueConstants.ALL_STAGES, value: valueConstants.ALL_STAGES },
        ...allNationalStages.map((stage) => ({
            label: this.formatStageOptionLabel(stage.label, stage.value),
            value: stage.value,
        })),
    ])

    readonly productOptions = signal<IFilterOption[]>([])
    readonly cityOptions = signal<IFilterOption[]>([])

    // Sorting state
    sortColumn = signal<string>('') // schoolName | stage | leadAgeing | potentialRevenue | lastContacted
    sortDirection = signal<ETableSortDirection>(ETableSortDirection.ASC)

    // Computed: filtered deals
    readonly filteredDeals = computed(() => {
        const stage = this.stageFilter()
        const product = this.productFilter()
        const city = this.cityFilter()
        const searchTerm = this.schoolNameSearch().toLowerCase().trim()
        const dealsData = this._deals()

        return dealsData.filter((deal) => {
            const matchesStage = stage === valueConstants.ALL_STAGES || deal.stageCode === stage
            const matchesProduct =
                product === valueConstants.ALL_PRODUCTS ||
                (deal.product !== valueConstants.NA && deal.product.split(', ').includes(product))
            const matchesCity = city === valueConstants.ALL_CITIES || deal.city === city
            const matchesSearch =
                !searchTerm ||
                (deal.schoolName && deal.schoolName.toLowerCase().includes(searchTerm))
            return matchesStage && matchesProduct && matchesCity && matchesSearch
        })
    })

    // Computed: sorted + filtered deals
    readonly sortedDeals = computed(() => {
        const items = [...this.filteredDeals()]
        const column = this.sortColumn()
        const direction = this.sortDirection()
        if (!column) {
            return items
        }
        const factor = direction === ETableSortDirection.ASC ? 1 : -1
        const safeStr = (v: unknown) => (v ?? '').toString().toLowerCase()
        const parseLakhs = (value: string) => {
            if (!value || value === this.valueConstants.NA) return Number.NEGATIVE_INFINITY
            const stripped = value.replace(/[₹L\s]/g, '').trim()
            const n = parseFloat(stripped)
            return isNaN(n) ? Number.NEGATIVE_INFINITY : n
        }
        const parseAgeing = (value: string | number) => {
            if (value === null || value === undefined) return Number.NEGATIVE_INFINITY
            const str = String(value)
            if (!str || str === this.valueConstants.NA) return Number.NEGATIVE_INFINITY
            const m = str.match(/(\d+)/)
            return m ? parseInt(m[1], 10) : Number.NEGATIVE_INFINITY
        }
        const parseDate = (value: string) => {
            if (!value || value === this.valueConstants.NA) return Number.NEGATIVE_INFINITY
            const t = Date.parse(value)
            return isNaN(t) ? Number.NEGATIVE_INFINITY : t
        }
        items.sort((a, b) => {
            switch (column) {
                case 'schoolName':
                    return factor * safeStr(a.schoolName).localeCompare(safeStr(b.schoolName))
                case 'stage':
                    // Sort by stageId ascending/descending
                    return factor * ((a.stageId || 0) - (b.stageId || 0))
                case 'leadAgeing':
                    return factor * (parseAgeing(a.leadAgeing) - parseAgeing(b.leadAgeing))
                case 'potentialRevenue':
                    return (
                        factor * (parseLakhs(a.potentialRevenue) - parseLakhs(b.potentialRevenue))
                    )
                case 'lastContacted':
                    return factor * (parseDate(a.lastContacted) - parseDate(b.lastContacted))
                default:
                    return 0
            }
        })
        return items
    })

    toggleSort(column: string): void {
        if (this.sortColumn() === column) {
            this.sortDirection.set(
                this.sortDirection() === ETableSortDirection.ASC
                    ? ETableSortDirection.DESC
                    : ETableSortDirection.ASC,
            )
        } else {
            this.sortColumn.set(column)
            this.sortDirection.set(ETableSortDirection.ASC)
        }
    }

    readonly title = computed(() => {
        return this.isTeamView
            ? valueConstants.ZONE_DEALS_PIPELINE_TITLE
            : valueConstants.DEALS_PIPELINE_TITLE
    })

    // Computed: total pipeline value (s3 to s6)
    readonly totalPipelineValue = computed(() => {
        const filtered = this.filteredDeals().filter((deal) => {
            const stageId = deal.stageId
            return (
                stageId >= EDealsPipelineStageRange.PIPELINE_VALUE_START &&
                stageId <= EDealsPipelineStageRange.PIPELINE_VALUE_END
            )
        })

        const total = filtered.reduce((sum, deal) => {
            const revenueStr = deal.potentialRevenue.replace(/[₹L\s]/g, '').trim()
            const value = parseFloat(revenueStr)
            return sum + (isNaN(value) ? 0 : value * AppConstants.LAKH_CONVERSION_CONSTANT)
        }, 0)

        return SalesFunnelHelper.formatCurrencyInLakhs(total)
    })

    constructor() {
        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            this.loadDealsPipelineData()
        })
    }

    ngOnInit(): void {
        this.dropdownCoordinator.opened$
            .pipe(
                filter((id) => id !== HOME_DROPDOWN_SOURCE.DEALS_PIPELINE),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.closeAllDropdowns())
        this.loadDealsPipelineData()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isTeamView'] && !changes['isTeamView'].firstChange) {
            this.loadDealsPipelineData()
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe()
        this.destroy$.next()
        this.destroy$.complete()
    }

    private unsubscribe(): void {
        if (this.dealsPipelineSubscription) {
            this.dealsPipelineSubscription.unsubscribe()
            this.dealsPipelineSubscription = undefined
        }
    }

    private loadDealsPipelineData(): void {
        const userData = this.userStore.getUserData()
        if (!userData?.user_id) {
            this._error.set('User ID not available')
            return
        }

        this._isLoading.set(true)
        this._error.set(null)
        this.unsubscribe()

        const view = this.isTeamView
            ? valueConstants.TEAM_VIEW_QUERY_PARAM
            : valueConstants.USER_VIEW_QUERY_PARAM

        this.dealsPipelineSubscription = this.dashboardService
            .getDealsPipelineData(userData.user_id, view)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success && response.data) {
                        const transformedDeals = this.transformApiDataToDeals(response.data)
                        this._deals.set(transformedDeals)
                        this.updateFilterOptions(response.data)
                    } else {
                        this._error.set('Failed to load deals pipeline data')
                    }
                    this._isLoading.set(false)
                },
                error: () => {
                    this._error.set('Failed to load deals pipeline data')
                    this._isLoading.set(false)
                },
            })
    }

    private transformApiDataToDeals(apiData: IDealsPipelineApiItem[]): IDeal[] {
        return apiData.map((item) => {
            const stageCode = `S${item.stage_id}`
            const potentialRevenue = this.formatRevenueInLakhs(item.potential_revenue)
            const product = this.formatProducts(item.pitched_products)
            const city = (item.city || valueConstants.NA).trim()
            const lastContacted = item.last_interaction || valueConstants.NA
            const leadAgeing = this.formatLeadAgeing(item.days_in_current_stage)
            const schoolName = (item.organisation_name || valueConstants.NA).trim()

            return {
                schoolName,
                organisationCode: item.organisation_code,
                city: city,
                stage: item.stage_name || valueConstants.NA,
                stageCode: stageCode,
                product: product,
                potentialRevenue: potentialRevenue,
                lastContacted: lastContacted,
                stageId: item.stage_id,
                leadAgeing: leadAgeing,
            }
        })
    }

    /** Navigate to the view client page (client dashboard detail) for the given organisation code. */
    navigateToViewClient(organisationCode: number): void {
        if (!organisationCode) {
            return
        }
        this.router.navigate(['/crm/view-client', String(organisationCode)])
    }

    private formatRevenueInLakhs(revenueStr: string): string {
        if (!revenueStr || revenueStr === '0' || revenueStr === '0.00') {
            return valueConstants.NA
        }

        const revenue = parseFloat(revenueStr)
        if (isNaN(revenue) || revenue === 0) {
            return valueConstants.NA
        }

        return SalesFunnelHelper.formatCurrencyInLakhs(revenue)
    }

    private formatProducts(products: string[]): string {
        if (!products || !products.length) {
            return valueConstants.NA
        }
        return products.join(', ')
    }

    private formatLeadAgeing(days: number | null | undefined): string {
        if (days === null || days === undefined) {
            return valueConstants.NA
        }
        const safeDays = Math.max(0, Math.floor(days))
        return `${safeDays} days`
    }

    private updateFilterOptions(apiData: IDealsPipelineApiItem[]): void {
        const uniqueProducts = new Set<string>()
        const uniqueCities = new Set<string>()
        const uniqueStages = new Map<string, string>()

        apiData.forEach((item) => {
            if (item.pitched_products && item.pitched_products.length) {
                item.pitched_products.forEach((product) => uniqueProducts.add(product))
            }
            if (item.city) {
                uniqueCities.add(item.city.trim())
            }
            if (item.stage_id !== null && item.stage_id !== undefined) {
                const stageCode = `S${item.stage_id}`
                if (!uniqueStages.has(stageCode)) {
                    uniqueStages.set(stageCode, item.stage_name || stageCode)
                }
            }
        })

        const stageOptions: IFilterOption[] = [
            { label: valueConstants.ALL_STAGES, value: valueConstants.ALL_STAGES },
            ...Array.from(uniqueStages.entries())
                .sort(([aCode], [bCode]) => aCode.localeCompare(bCode))
                .map(([code, name]) => ({
                    label: `${this.formatStageCode(code)} - ${name}`,
                    value: code,
                })),
        ]

        const productOptions: IFilterOption[] = [
            { label: valueConstants.ALL_PRODUCTS, value: valueConstants.ALL_PRODUCTS },
            ...Array.from(uniqueProducts)
                .sort()
                .map((product) => ({ label: product, value: product })),
        ]

        const cityOptions: IFilterOption[] = [
            { label: valueConstants.ALL_CITIES, value: valueConstants.ALL_CITIES },
            ...Array.from(uniqueCities)
                .sort()
                .map((city) => ({ label: city, value: city })),
        ]

        this.stageOptions.set(stageOptions)
        this.productOptions.set(productOptions)
        this.cityOptions.set(cityOptions)
    }

    formatStageCode(codeOrLabel: string): string {
        if (!codeOrLabel) {
            return codeOrLabel
        }
        return SalesFunnelHelper.getStageDisplayCodeFromCode(codeOrLabel)
    }

    private formatStageOptionLabel(label: string, value: string): string {
        const formattedValue = this.formatStageCode(value)
        return label.startsWith(value) ? label.replace(value, formattedValue) : label
    }

    // Dropdown toggle method
    toggleDropdown(type: EDealsPipelineFilterType): void {
        this.dropdownCoordinator.notifyOpened(HOME_DROPDOWN_SOURCE.DEALS_PIPELINE)
        this.isStageDropdownOpen.set(
            type === EDealsPipelineFilterType.STAGE ? !this.isStageDropdownOpen() : false,
        )
        this.isProductDropdownOpen.set(
            type === EDealsPipelineFilterType.PRODUCT ? !this.isProductDropdownOpen() : false,
        )
        this.isCityDropdownOpen.set(
            type === EDealsPipelineFilterType.CITY ? !this.isCityDropdownOpen() : false,
        )
    }

    // Filter selection methods
    selectStageFilter(value: string): void {
        this.stageFilter.set(value)
        this.isStageDropdownOpen.set(false)
    }

    selectProductFilter(value: string): void {
        this.productFilter.set(value)
        this.isProductDropdownOpen.set(false)
    }

    selectCityFilter(value: string): void {
        this.cityFilter.set(value)
        this.isCityDropdownOpen.set(false)
    }

    // Close dropdowns when clicking outside
    closeAllDropdowns(): void {
        this.isStageDropdownOpen.set(false)
        this.isProductDropdownOpen.set(false)
        this.isCityDropdownOpen.set(false)
    }

    onSearchChange(value: string): void {
        this.schoolNameSearch.set(value)
    }
}
